import { useState, useMemo } from "react"
import { useSearchParams, useNavigate, useLocation } from "react-router-dom"
import { Form } from "antd"

// components
import TextInput from "components-design-system/form-fields/TextInput/TextInput"
import { PrimeButton } from "components-design-system"
import ErrorNotification from "../ErrorNotification/ErrorNotification"

// apis
import { postResetPasswordWithToken, postResetPasswordWithTokenAndOldPassword } from "api/hooks/auth/useAuthApis"

// utilities
import useAppState from "context/hooks/useAppState"

// styles
import "./PasswordReset.scss"

const PasswordReset = () => {
  const [form] = Form.useForm()
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const location = useLocation()
  const [error, setError] = useState(null)
  const { openToastNotification } = useAppState()

  const token = useMemo(() => {
    return searchParams.get("token")
  }, [searchParams])

  const isOldPasswordVisible = location.state?.isOldPasswordVisible || false

  const handleResetPasswordWithToken = async () => {
    const { newPassword } = await form.getFieldValue()
    try {
      setIsSubmitLoading(true)
      await postResetPasswordWithToken({ password: newPassword, token })
      navigate("/login")
      setError(null)
    } catch (error) {
      if (error?.response?.data?.error) {
        setError(error)
      }
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const handleResetPasswordWithTokenAndOldPassword = async () => {
    const { oldPassword, newPassword, confirmPassword } = await form.getFieldValue()
    try {
      setIsSubmitLoading(true)
      await postResetPasswordWithTokenAndOldPassword(
        {
          old_password: oldPassword,
          new_password: newPassword,
          new_password2: confirmPassword,
        },
        location.state?.accessTempToken
      )
      setError(null)
    } catch (error) {
      if (error?.response?.status === 307) {
        navigate("/login")
        openToastNotification("success", "Success", "Password reset successfully")
        setError(null)
      } else {
        setError(error)
      }
    } finally {
      setIsSubmitLoading(false)
    }
  }

  return (
    <div className="prime-password-reset">
      <div className="right-container">
        <Form
          form={form}
          onFinish={isOldPasswordVisible ? handleResetPasswordWithTokenAndOldPassword : handleResetPasswordWithToken}
        >
          <div className="content-container">
            <div className="login-header">Reset your password</div>
            <div className="input-container">
              {isOldPasswordVisible && (
                <Form.Item
                  name="oldPassword"
                  style={{ marginBottom: "0px" }}
                  rules={[{ required: true }]}
                >
                  <TextInput
                    type="password"
                    placeholder="Old password"
                    prefix={<i className="fal fa-lock-alt login-input-icon" />}
                    style={{ marginTop: 12 }}
                  />
                </Form.Item>
              )}
              <Form.Item
                name="newPassword"
                style={{ marginBottom: "0px" }}
                rules={[{ required: true }]}
              >
                <TextInput
                  type="password"
                  placeholder="New password"
                  prefix={<i className="fal fa-lock-alt login-input-icon" />}
                  style={{ marginTop: 12 }}
                />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                dependencies={["newPassword"]}
                style={{ marginBottom: "0px" }}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error("Your password does not match."))
                    },
                  }),
                ]}
              >
                <TextInput
                  type="password"
                  placeholder="Confirm password"
                  prefix={<i className="fal fa-lock-alt login-input-icon" />}
                  style={{ marginTop: 12 }}
                />
              </Form.Item>
              <div className="helper-text">
                Password must use at least 10 characters and contain one uppercase letter, one lowercase letter, one
                special character, and one number.
              </div>
              <p className="Back-to-login-link">
                <span
                  className="Back-to-login-span"
                  onClick={() => navigate("/login")}
                >
                  Back to login
                </span>
              </p>
              <PrimeButton
                type="primary"
                htmlType="submit"
                style={{ marginTop: 24 }}
                loading={isSubmitLoading}
              >
                Update & Log in
              </PrimeButton>
              <ErrorNotification error={error} />
            </div>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default PasswordReset
